import { useState } from "react";
import FifaVerifiedBadge from "../common/FifaVerifiedBadge";
import {
  Popover,
  PopoverItem,
  MenuDivider,
  TooltipArrow,
  Sheet,
  SheetSectionGap,
  Button,
  PersonAvatar,
  UserIcon,
  PencilIcon,
  ShieldIcon,
  TrashIcon,
  CheckIcon,
} from "./v2";

// Agent overflow menu — V2 rebuild 1:1 from ARTBOARD-SPEC board 1g
// (desktop CMP-POPOVER) and board 1m (mobile bottom action sheet), plus
// the board-1k demo-3 last-admin guard (FOLLOWUP PACK B / F-8).
//
// Wiring is byte-identical to the previous version (FUNCTION-INVENTORY
// §2.5): onMakeAdmin / onRemoveAdmin open the existing confirm flow,
// onRemoveFromAgency routes to the remove flow (the artboard's remove
// confirmation dialog lives in AgentsGrid; the handler behind it is the
// unchanged removeAgentFromAgency call). "View profile" reuses the
// existing card-header navigation handler.
//
// Follow-up items wired here (FOLLOWUP-PACK-BRIEF PACK B):
// - "Edit details" (F-5): menu entry point to the EXISTING display-title
//   editor on the card (DisplayTitleEditor / setAgentDisplayTitle) — the
//   only member-detail edit surface that exists. No new edit surface.
// - Own-card self menu (F-8, board 1k demo 3): "Remove as admin" on the
//   current user's own card — DISABLED with the 1k tooltip ("You're the
//   only admin…") when they are the only admin (adminCount derived
//   client-side from the loaded roster), otherwise wired to the existing
//   demote confirm flow (self-demote warning + 1200 ms reload survive in
//   useAgencyProfile). The backend last-admin guard (400 → toast) stays
//   authoritative.
// - "Submit license" (B4, quiet affordance — NOT a reminder): shown only
//   on the current user's OWN card while unlicensed; opens the EXISTING
//   license submission flow (LicenseUpdateModal → updateAgentLicense →
//   POST /users/update-agent-license/ — same mechanism as My Profile).
//   No banner, no badge, no nagging; unlicensed is a legitimate state.
//
// "Send license reminder" (F-4 original) stays NOT rendered — reframed
// by Marin as the B4 self-serve affordance above (no reminder logic).

// Board 1k tooltip: bg #0F172A, 12px lh1.5, r8, padding 8px 11px, width
// 196, anchored to the guarded item's right edge + GLY-05 arrow.
const GuardTooltip = ({ children }) => (
  <span
    role="tooltip"
    className="absolute z-10"
    style={{
      left: "104%",
      top: 0,
      width: 196,
      background: "#0F172A",
      color: "#fff",
      fontSize: 12,
      lineHeight: 1.5,
      borderRadius: 8,
      padding: "8px 11px",
      boxShadow: "0 8px 24px rgba(15,23,42,.3)",
    }}
  >
    <TooltipArrow style={{ top: 16 }} />
    {children}
  </span>
);

const LAST_ADMIN_TOOLTIP =
  "You’re the only admin. Make someone else an admin first.";

const AgentCardPopover = ({
  isOpen,
  onClose,
  agent,
  onViewProfile,
  onEditDetails,
  onMakeAdmin,
  onRemoveAdmin,
  onRemoveFromAgency,
  onSubmitLicense,
  isLoading = false,
  isAgentAnAdmin = false,
  isOwnCard = false,
  isLastAdmin = false,
  mobile = false,
  // Kept for callers that still pass it (internal signal); the sheet
  // header no longer renders a credential from it — see `verified`.
  licensed = false,
  // D-A: the FIFA Verified badge replaces the "Licensed" word. True only
  // when license_verification_status === 'fifa_verified'.
  verified = false,
  managingCount = 0,
}) => {
  // Board 1k: the guard tooltip shows on hover AND on tap (the item stays
  // visible so the rule is learnable; the tooltip explains the way out).
  const [guardTipVisible, setGuardTipVisible] = useState(false);

  if (!isOpen) return null;

  const act = (fn) => () => {
    if (isLoading) return;
    fn(agent);
    onClose();
  };

  // Self-demote is guarded client-side only for presentation; enabled it
  // routes into the EXISTING demote confirm flow (backend 400 stays
  // authoritative).
  const selfDemoteDisabled = isLoading || isLastAdmin;

  if (mobile) {
    // Board 1m — agent actions bottom sheet: grabber, context header,
    // action rows (17px icons, 15px labels), section gap, destructive
    // row, Cancel.
    const row =
      "flex items-center gap-3 py-3.5 px-1 text-[15px] cursor-pointer";
    return (
      <Sheet
        open={isOpen}
        onClose={onClose}
        hideClose
        padding="10px 16px 40px"
        grabberMargin={10}
      >
        <div
          className="flex items-center gap-[11px] border-b border-[#EDF1F6]"
          style={{ padding: "6px 4px 14px" }}
        >
          <PersonAvatar
            src={agent?.profile_picture_url || null}
            name={agent?.name}
            seed={agent?.account_id}
            size={38}
          />
          <span className="min-w-0 flex flex-col gap-[1px]">
            <span className="text-[14.5px] font-semibold text-[#0F172A] truncate">
              {agent?.name}
            </span>
            {/* Sub-line: FIFA Verified badge (gate: isFifaVerified —
                Marin 2026-08-04, self-declaration carries it, failed_qr
                hides it) + "N players"; no badge renders no credential
                at all (board 2g rule carries over). */}
            <span className="text-xs text-[#64748B] inline-flex items-center gap-[6px]">
              {verified && <FifaVerifiedBadge size="sm" />}
              <span>
                <span data-no-translate>{managingCount} </span>
                {managingCount === 1 ? "player" : "players"}
              </span>
            </span>
          </span>
        </div>

        <div
          role="button"
          className={`${row} text-[#334155] border-b border-[#F5F7FA]`}
          onClick={act(onViewProfile)}
        >
          <UserIcon size={17} />
          View profile
        </div>
        {onEditDetails && (
          <div
            role="button"
            className={`${row} text-[#334155] border-b border-[#F5F7FA]`}
            onClick={act(onEditDetails)}
          >
            <PencilIcon size={17} />
            Edit details
          </div>
        )}
        {isOwnCard && onSubmitLicense && (
          <div
            role="button"
            className={`${row} text-[#334155] border-b border-[#F5F7FA]`}
            onClick={act(onSubmitLicense)}
          >
            <CheckIcon size={17} stroke="#64748B" strokeWidth={2} />
            Submit license
          </div>
        )}
        {isOwnCard && isAgentAnAdmin ? (
          // F-8 self-demote row (board 1k demo 3, mobile adaptation: the
          // tooltip copy renders inline under the disabled row on tap).
          <>
            <div
              role="button"
              aria-disabled={selfDemoteDisabled || undefined}
              className={`flex items-center gap-3 py-3.5 px-1 text-[15px] ${
                selfDemoteDisabled
                  ? "text-[#94A3B8] cursor-not-allowed"
                  : "text-[#334155] cursor-pointer"
              }`}
              onClick={
                isLastAdmin
                  ? () => setGuardTipVisible((v) => !v)
                  : act(onRemoveAdmin)
              }
            >
              <ShieldIcon
                size={17}
                stroke={selfDemoteDisabled ? "#C4CEDA" : "#64748B"}
              />
              Remove as admin
            </div>
            {isLastAdmin && guardTipVisible && (
              <div
                role="tooltip"
                className="text-white"
                style={{
                  background: "#0F172A",
                  fontSize: 12,
                  lineHeight: 1.5,
                  borderRadius: 8,
                  padding: "8px 11px",
                  margin: "0 4px 8px",
                }}
              >
                {LAST_ADMIN_TOOLTIP}
              </div>
            )}
          </>
        ) : isOwnCard ? null : !isAgentAnAdmin ? (
          <div
            role="button"
            className={`${row} text-[#334155]`}
            onClick={act(onMakeAdmin)}
          >
            <ShieldIcon size={17} />
            Make admin
          </div>
        ) : (
          <div
            role="button"
            className={`${row} text-[#334155]`}
            onClick={act(onRemoveAdmin)}
          >
            <ShieldIcon size={17} />
            Remove as admin
          </div>
        )}

        {!isOwnCard && (
          <>
            <SheetSectionGap />

            <div
              role="button"
              className={`${row} font-medium text-[#DC2626]`}
              onClick={act(onRemoveFromAgency)}
            >
              <TrashIcon size={17} />
              Remove from agency
            </div>
          </>
        )}

        <Button
          mobile
          variant="sheetCancel"
          onClick={onClose}
          style={{ marginTop: 10 }}
        >
          Cancel
        </Button>
      </Sheet>
    );
  }

  // Board 1g — desktop CMP-POPOVER 216px anchored to the dots button.
  return (
    <Popover
      onClose={onClose}
      width={216}
      className="absolute"
      style={{ right: 8, top: 56, zIndex: 20, overflow: "visible" }}
    >
      <PopoverItem
        icon={<UserIcon size={14} />}
        disabled={isLoading}
        onClick={act(onViewProfile)}
      >
        View profile
      </PopoverItem>
      {onEditDetails && (
        <PopoverItem
          icon={<PencilIcon size={14} />}
          disabled={isLoading}
          onClick={act(onEditDetails)}
        >
          Edit details
        </PopoverItem>
      )}
      {isOwnCard && onSubmitLicense && (
        <PopoverItem
          icon={<CheckIcon size={14} stroke="#64748B" strokeWidth={2} />}
          disabled={isLoading}
          onClick={act(onSubmitLicense)}
        >
          Submit license
        </PopoverItem>
      )}
      {isOwnCard && isAgentAnAdmin ? (
        // F-8 — board 1k demo 3: the self-demote item stays VISIBLE; when
        // the current user is the only admin it is disabled with the
        // explanatory tooltip (hover/tap), otherwise it enters the
        // existing demote confirm flow (self-demote warning + reload).
        <div
          className="relative"
          onMouseEnter={isLastAdmin ? () => setGuardTipVisible(true) : undefined}
          onMouseLeave={isLastAdmin ? () => setGuardTipVisible(false) : undefined}
        >
          <PopoverItem
            icon={
              <ShieldIcon
                size={14}
                stroke={selfDemoteDisabled ? "#C4CEDA" : "#64748B"}
              />
            }
            disabled={selfDemoteDisabled}
            onClick={
              isLastAdmin
                ? () => setGuardTipVisible(true)
                : act(onRemoveAdmin)
            }
          >
            Remove as admin
          </PopoverItem>
          {isLastAdmin && guardTipVisible && (
            <GuardTooltip>{LAST_ADMIN_TOOLTIP}</GuardTooltip>
          )}
        </div>
      ) : isOwnCard ? null : !isAgentAnAdmin ? (
        <PopoverItem
          icon={<ShieldIcon size={14} />}
          disabled={isLoading}
          onClick={act(onMakeAdmin)}
        >
          Make admin
        </PopoverItem>
      ) : (
        <PopoverItem
          icon={<ShieldIcon size={14} />}
          disabled={isLoading}
          onClick={act(onRemoveAdmin)}
        >
          Remove as admin
        </PopoverItem>
      )}
      {!isOwnCard && (
        <>
          <MenuDivider />
          <PopoverItem
            icon={<TrashIcon size={14} />}
            destructive
            disabled={isLoading}
            onClick={act(onRemoveFromAgency)}
          >
            Remove from agency
          </PopoverItem>
        </>
      )}
    </Popover>
  );
};

export default AgentCardPopover;
